import { DASHBOARD_API_BASE_URL, USE_DASHBOARD_REAL_BACKEND, type DashboardApiResponse } from '../config/config'
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth'
import { notificationAssets } from '../../constants/assets'

export type NotificationCategory =
  | 'Bookings'
  | 'Customers'
  | 'Drivers'
  | 'Payments'
  | 'Reviews'
  | 'Reports'
  | 'System'

export type NotificationGroup = string

export type NotificationAction = {
  label: string
  variant?: 'primary' | 'danger' | 'success'
  path?: string
}

export type NotificationItem = {
  id: number | string
  group: NotificationGroup
  category: NotificationCategory
  title: string
  description: string
  time: string
  icon: string
  iconSize: string
  isNew?: boolean
  actions: NotificationAction[]
  booking_reference?: string | null
  is_read?: boolean
}

const NOTIFICATIONS_STORAGE_KEY = 'salzburg_dashboard_notifications_list'

const getAuthHeaders = (): HeadersInit => {
  const token = typeof window !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) : null
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/**
 * Fetch all Notifications (GET)
 */


type ApiNotification = {
  id: number | string
  title: string
  description: string
  booking_reference?: string | null
  created_at: string
  is_read: boolean
}

const mapNotification = (item: ApiNotification): NotificationItem => {
  const createdAt = new Date(item.created_at)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  const group = sameDay(createdAt, today)
    ? `Today–${createdAt.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })}`
    : sameDay(createdAt, yesterday)
      ? 'Yesterday'
      : createdAt.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })

  return {
    id: item.id,
    title: item.title,
    description: item.description,
    booking_reference: item.booking_reference ?? null,
    is_read: item.is_read,
    isNew: !item.is_read,
    group,
    category: 'Bookings',
    time: createdAt.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    }),
    icon: notificationAssets.sideBox1,
    iconSize: 'h-[20px] w-[20px]',
    actions: [],
  }
}


export const apiGetNotifications = async (): Promise<DashboardApiResponse<NotificationItem[]>> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    return {
      success: true,
      message: 'Notifications loaded',
      data: [],
    }
  }

  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/notifications`, {
      method: 'GET',
      headers: getAuthHeaders(),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      if (Array.isArray(result?.data)) {
        const items = (result.data as ApiNotification[]).map(mapNotification)
        localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(items))
        return {
          success: true,
          message: 'Notifications fetched successfully',
          data: items,
        }
      }
    }
  } catch {}

  // Fallback to localStorage or empty
  try {
    const saved = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
    if (saved) {
      return {
        success: true,
        message: 'Notifications loaded',
        data: JSON.parse(saved),
      }
    }
  } catch {}

  return {
    success: true,
    message: 'Notifications ready',
    data: [],
  }
}

/**
 * Mark a single Notification as read (POST)
 */
export const apiMarkNotificationAsRead = async (
  id: number | string
): Promise<DashboardApiResponse> => {
  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/notifications/${id}/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      return {
        success: true,
        message: result?.message || 'Notification marked as read',
      }
    }
  } catch {}

  return {
    success: false,
    message: 'Failed to mark notification as read',
  }
}

/**
 * Mark all Notifications as read (POST)
 */
export const apiMarkAllNotificationsAsRead = async (): Promise<DashboardApiResponse> => {
  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/notifications/mark-all-read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      return {
        success: true,
        message: result?.message || 'All notifications marked as read',
      }
    }
  } catch {}

  return {
    success: false,
    message: 'Failed to mark all notifications as read',
  }
}

/**
 * Delete a Notification (DELETE)
 */
export const apiDeleteNotification = async (
  id: number | string
): Promise<DashboardApiResponse> => {
  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/notifications/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      return {
        success: true,
        message: result?.message || 'Notification deleted',
      }
    }
  } catch {}

  return {
    success: false,
    message: 'Failed to delete notification',
  }
}
